import { Conversion } from "@/types/dashboard.types";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

Font.register({
  family: "Helvetica",
  src: "/Helvetica.ttf",
});

const colors = {
  primary: "#3366CC",
  secondary: "#2c3e50",
  tertiary: "#7f8c8d",
  white: "#FFFFFF",
  lightGray: "#f9f9f9",
  mediumGray: "#e2e1e1",
};

const fontSizes = {
  xs: 8,
  sm: 10,
  md: 12,
};

const ROWS_PER_PAGE = 25;

const styles = StyleSheet.create({
  body: {
    paddingTop: 15,
    paddingBottom: 0,
    paddingHorizontal: 40,
    fontFamily: "Helvetica",
    position: "relative",
  },
  watermark: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
  },
  watermarkImage: {
    width: "100%",
    height: "100%",
  },
  footer: {
    position: "absolute",
    fontSize: fontSizes.xs,
    bottom: "3rem",
    left: 0,
    right: 0,
    textAlign: "center",
    color: colors.tertiary,
  },
  pageNumber: {
    position: "absolute",
    fontSize: fontSizes.xs,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
    color: colors.tertiary,
  },
  table: {
    width: "100%",
    marginBottom: 15,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    paddingHorizontal: 2,
    borderBottomColor: colors.lightGray,
    borderBottomStyle: "solid",
    alignItems: "center",
    height: 20,
  },
  tableCol: {
    width: "20%",
  },
  tableCell: {
    margin: "auto",
    fontSize: fontSizes.xs,
  },
  text: {
    color: colors.secondary,
    fontSize: fontSizes.xs,
  },
  textBold: {
    fontWeight: "bold",
  },
  textWhite: {
    color: colors.white,
  },
  studentInfoSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 80,
    marginBottom: 15,
    padding: 10,
  },
  genieSaysSection: {
    marginTop: 15,
    marginBottom: 15,
  },
  genieSaysSummary: {
    fontSize: fontSizes.xs,
    lineHeight: 1.5,
    color: colors.secondary,
  },
  sectionTitle: {
    color: colors.primary,
    fontSize: fontSizes.sm,
    fontWeight: "bold",
    marginBottom: 5,
  },
  divider: {
    backgroundColor: colors.primary,
    height: 1,
    marginBottom: 10,
  },
  flexRow: {
    flexDirection: "row",
  },
  flexCol: {
    flexDirection: "column",
  },
  halfWidth: {
    width: "50%",
  },
  fullWidth: {
    width: "100%",
  },
  marginTop100: {
    marginTop: 50,
  },
  padding5: {
    padding: 5,
  },
  marginBottom5: {
    marginBottom: 5,
  },
  labeledField: {
    marginBottom: 8,
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  labeledFieldLabel: {
    fontSize: fontSizes.xs,
    color: colors.tertiary,
    marginBottom: 2,
  },
  labeledFieldValue: {
    fontSize: fontSizes.sm,
    color: colors.secondary,
    fontWeight: "bold",
  },
});

function LabeledField({
  label,
  value,
  minWidth,
}: {
  label: string;
  value: string;
  minWidth?: number;
}) {
  return (
    <View style={[styles.fullWidth, { minWidth }]}>
      <View style={styles.labeledField}>
        <Text style={styles.labeledFieldLabel}>{label}</Text>
        <Text style={styles.labeledFieldValue}>{value}</Text>
      </View>
    </View>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.divider} />
    </>
  );
}

function StudentInfoSection({
  conversion,
}: {
  conversion: {
    conversion: Conversion["conversion"];
    studentInformation: Conversion["studentInformation"] | null;
  };
}) {
  return (
    <View style={styles.studentInfoSection}>
      <View style={[styles.flexRow, { flexWrap: "wrap", gap: 8 }]}>
        <View style={{ width: "30%" }}>
          <LabeledField
            label="Name"
            value={conversion.studentInformation?.name ?? ""}
          />
        </View>
        <View style={{ width: "30%" }}>
          <LabeledField
            label="Country"
            value={conversion.studentInformation?.country ?? ""}
          />
        </View>
        <View style={{ width: "30%" }}>
          <LabeledField
            label="Curriculum"
            value={conversion.studentInformation?.scale ?? ""}
          />
        </View>
        <View style={{ width: "30%" }}>
          <LabeledField
            label="GPA"
            value={conversion.conversion.cumulativeGPA ?? ""}
          />
        </View>
      </View>
    </View>
  );
}

function TableHeader({ hasCredits }: { hasCredits: boolean }) {
  const headers = [
    "Subject",
    "Grade",
    ...(hasCredits ? ["Credits"] : []),
    "U.S Grade Points",
  ];
  return (
    <View style={[styles.tableRow, { backgroundColor: colors.primary }]}>
      {headers.map((header, index) => (
        <View
          key={index}
          style={[
            styles.tableCol,
            { width: header === "Subject" ? "70%" : "20%" },
          ]}
        >
          <Text
            style={[
              styles.text,
              styles.textBold,
              styles.textWhite,
              {
                textAlign: index === 0 ? "left" : "center",
              },
            ]}
          >
            {header}
          </Text>
        </View>
      ))}
    </View>
  );
}

function TableRow({
  item,
  index,
  hasCredits,
}: {
  item: any;
  index: number;
  hasCredits: boolean;
}) {
  return (
    <View
      style={[
        styles.tableRow,
        { backgroundColor: index % 2 === 0 ? colors.white : colors.lightGray },
        styles.padding5,
      ]}
    >
      {["subject", "score", ...(hasCredits ? ["credits"] : []), "points"].map(
        (key, idx) => (
          <View
            key={idx}
            style={[
              styles.tableCol,
              { width: key === "subject" ? "70%" : "20%" },
            ]}
          >
            <Text
              style={{
                ...styles.text,
                textAlign: idx === 0 ? "left" : "center",
                flexWrap: "wrap",
              }}
            >
              {item[key]}
            </Text>
          </View>
        )
      )}
    </View>
  );
}

function TranscriptTable({
  data,
  showCumulative = false,
  cumulativeGPA = "",
}: {
  data: Array<{
    subject: string;
    score: string;
    points: string | number;
    credits: string | null;
  }>;
  showCumulative?: boolean;
  cumulativeGPA?: string | number;
}) {
  const hasCredits = data.some((item) => item.credits);
  return (
    <View style={styles.table}>
      <TableHeader hasCredits={hasCredits} />
      <View>
        {data.map((item, idx) => (
          <TableRow key={idx} item={item} index={idx} hasCredits={hasCredits} />
        ))}

        {showCumulative && (
          <View
            style={[styles.tableRow, { backgroundColor: colors.mediumGray }]}
          >
            <View style={[styles.tableCol, { width: "70%", paddingLeft: 3 }]}>
              <Text style={[styles.text, styles.textBold]}>Cumulative</Text>
            </View>
            <View style={[styles.tableCol, { width: "20%" }]}>
              <Text style={[styles.text, { textAlign: "center" }]}></Text>
            </View>
            {hasCredits && (
              <View style={[styles.tableCol, { width: "20%" }]}>
                <Text style={[styles.text, { textAlign: "center" }]}></Text>
              </View>
            )}
            <View style={[styles.tableCol, { width: "20%" }]}>
              <Text
                style={[styles.text, styles.textBold, { textAlign: "center" }]}
              >
                {String(cumulativeGPA)}
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

export function Template({
  props,
}: {
  props: {
    conversion: Conversion["conversion"];
    studentInformation: Conversion["studentInformation"] | null;
  };
}) {
  const evaluationItems = props.conversion.table || [];
  const totalPages = Math.ceil(evaluationItems.length / ROWS_PER_PAGE);

  return (
    <Document style={styles.fullWidth}>
      <Page style={styles.body}>
        <View style={styles.watermark}>
          <Image src="/pdf-bg.jpg" style={styles.watermarkImage} />
        </View>

        {props.studentInformation?.name ? (
          <StudentInfoSection conversion={props} />
        ) : null}

        <View
          style={
            props.studentInformation?.name
              ? {}
              : {
                  marginTop: 80,
                  marginBottom: 15,
                  padding: 10,
                }
          }
        >
          <SectionTitle title="Transcript" />

          <TranscriptTable
            data={evaluationItems.slice(0, ROWS_PER_PAGE)}
            showCumulative={evaluationItems.length <= ROWS_PER_PAGE}
            cumulativeGPA={props.conversion.cumulativeGPA}
          />

          {evaluationItems.length <= ROWS_PER_PAGE && (
            <GenieSaysSection conclusion={props.conversion.conclusion} />
          )}
        </View>

        <View style={styles.footer}>
          <Text>~ Generated By Transcript Genie ~</Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} / ${totalPages}`
            }
          />
        </View>
      </Page>

      {Array.from({ length: totalPages - 1 }, (_, i) => {
        const pageIndex = i + 1;
        const startIdx = pageIndex * ROWS_PER_PAGE;
        const endIdx = Math.min(
          (pageIndex + 1) * ROWS_PER_PAGE,
          evaluationItems.length
        );
        const isLastPage = pageIndex === totalPages - 1;

        return (
          <Page key={`page-${pageIndex}`} style={styles.body}>
            <View style={styles.watermark}>
              <Image src="/pdf-bg.jpg" style={styles.watermarkImage} />
            </View>

            <View
              style={{
                marginTop: 100,
              }}
            >
              <SectionTitle title={`Transcript (continued)`} />

              <TranscriptTable
                data={evaluationItems.slice(startIdx, endIdx)}
                showCumulative={isLastPage}
                cumulativeGPA={props.conversion.cumulativeGPA}
              />

              {isLastPage && (
                <GenieSaysSection conclusion={props.conversion.conclusion} />
              )}
            </View>

            <View style={styles.footer}>
              <Text>~ Generated By Transcript Genie ~</Text>
              <Text
                style={styles.pageNumber}
                render={({ pageNumber, totalPages }) =>
                  `${pageNumber} / ${totalPages}`
                }
              />
            </View>
          </Page>
        );
      })}
    </Document>
  );
}

function GenieSaysSection({ conclusion }: { conclusion: string }) {
  return (
    <View style={styles.genieSaysSection}>
      <SectionTitle title="Genie Says" />
      <Text style={styles.genieSaysSummary}>{conclusion}</Text>
    </View>
  );
}
